import React, { useState, useEffect } from 'react';
import WeeklyAvailabilityManager from './weekly-availability-manager';

interface TeamMemberCapacity {
  TeamMemberId: string;
  UserId: string;
  Name: string;
  Email: string;
  WeeklyHours: number;
  ProjectAllocationPercent: number;
}

interface TeamMemberCapacityManagerProps {
  teamId: string;
  onSuccess?: () => void;
}

export default function TeamMemberCapacityManager({ teamId, onSuccess }: TeamMemberCapacityManagerProps) {
  const [members, setMembers] = useState<TeamMemberCapacity[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);

  useEffect(() => {
    fetchTeamMembers();
  }, [teamId]);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const res = await fetch(`/teams/${teamId}`);
      const data = await res.json();

      if (data.success && data.data.members) {
        const capacityMembers = data.data.members.map((member: any) => ({
          TeamMemberId: member.TeamMemberId,
          UserId: member.UserId,
          Name: member.Name || 'Unknown',
          Email: member.Email || 'unknown@example.com',
          WeeklyHours: member.WeeklyHours || 40,
          ProjectAllocationPercent: member.ProjectAllocationPercent || 100,
        }));
        setMembers(capacityMembers);
      }
      setError('');
    } catch (err: any) {
      setError('Failed to load team members');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCapacity = async (memberId: string, weeklyHours: number, projectPercent: number) => {
    setSaving(memberId);
    try {
      const res = await fetch(`/teams/${teamId}/members/${memberId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          weeklyHours,
          projectAllocationPercent: projectPercent,
        }),
      });

      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to update');
      }

      setMembers(prev =>
        prev.map(m =>
          m.TeamMemberId === memberId
            ? { ...m, WeeklyHours: weeklyHours, ProjectAllocationPercent: projectPercent }
            : m
        )
      );

      if (onSuccess) onSuccess();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return <div style={{ padding: '1rem', textAlign: 'center' }}>Loading team members...</div>;
  }

  return (
    <div>
      {error && (
        <div style={{ backgroundColor: '#f8d7da', color: '#721c24', padding: '1rem', borderRadius: '4px', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {members.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', backgroundColor: '#f9f9f9', borderRadius: '4px', color: '#666' }}>
          <p>No team members assigned yet</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Member Name</th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Email</th>
                <th style={{ padding: '0.75rem', textAlign: 'center' }}>Weekly Hours</th>
                <th style={{ padding: '0.75rem', textAlign: 'center' }}>Project Allocation %</th>
                <th style={{ padding: '0.75rem', textAlign: 'center' }}>Available for Projects</th>
                <th style={{ padding: '0.75rem', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map(member => {
                const projectHours = (member.WeeklyHours * member.ProjectAllocationPercent) / 100;
                const isExpanded = expandedMemberId === member.TeamMemberId;
                return (
                  <React.Fragment key={member.TeamMemberId}>
                    <MemberRow
                      member={member}
                      projectHours={projectHours}
                      isSaving={saving === member.TeamMemberId}
                      isExpanded={isExpanded}
                      onUpdate={handleUpdateCapacity}
                      onToggleExpand={() =>
                        setExpandedMemberId(isExpanded ? null : member.TeamMemberId)
                      }
                    />
                    {isExpanded && (
                      <tr style={{ background: '#f9f9f9' }}>
                        <td colSpan={6} style={{ padding: '1.5rem' }}>
                          <WeeklyAvailabilityManager
                            memberId={member.TeamMemberId}
                            memberName={member.Name}
                            onSuccess={fetchTeamMembers}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface MemberRowProps {
  member: TeamMemberCapacity;
  projectHours: number;
  isSaving: boolean;
  isExpanded: boolean;
  onUpdate: (memberId: string, weeklyHours: number, projectPercent: number) => Promise<void>;
  onToggleExpand: () => void;
}

function MemberRow({ member, projectHours, isSaving, isExpanded, onUpdate, onToggleExpand }: MemberRowProps) {
  const [weeklyHours, setWeeklyHours] = useState(member.WeeklyHours);
  const [projectPercent, setProjectPercent] = useState(member.ProjectAllocationPercent);
  const [isEditing, setIsEditing] = useState(false);

  const handleSave = async () => {
    await onUpdate(member.TeamMemberId, weeklyHours, projectPercent);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <tr style={{ borderBottom: '1px solid #eee', background: '#f9f9f9' }}>
        <td style={{ padding: '0.75rem' }}>{member.Name}</td>
        <td style={{ padding: '0.75rem', fontSize: '0.9rem', color: '#666' }}>{member.Email}</td>
        <td style={{ padding: '0.75rem', textAlign: 'center' }}>
          <input
            type="number"
            min="0"
            max="100"
            step="0.5"
            value={weeklyHours}
            onChange={e => setWeeklyHours(Number(e.target.value))}
            style={{
              width: '80px',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              textAlign: 'center',
            }}
          />
        </td>
        <td style={{ padding: '0.75rem', textAlign: 'center' }}>
          <input
            type="number"
            min="0"
            max="100"
            step="1"
            value={projectPercent}
            onChange={e => setProjectPercent(Number(e.target.value))}
            style={{
              width: '80px',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              textAlign: 'center',
            }}
          />
          %
        </td>
        <td style={{ padding: '0.75rem', textAlign: 'center', fontWeight: 600, color: '#0066cc' }}>
          {projectHours.toFixed(1)}h
        </td>
        <td style={{ padding: '0.75rem', textAlign: 'center' }}>
          <button
            onClick={handleSave}
            disabled={isSaving}
            style={{
              padding: '0.5rem 1rem',
              marginRight: '0.5rem',
              background: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              opacity: isSaving ? 0.6 : 1,
            }}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
          <button
            onClick={() => {
              setWeeklyHours(member.WeeklyHours);
              setProjectPercent(member.ProjectAllocationPercent);
              setIsEditing(false);
            }}
            style={{
              padding: '0.5rem 1rem',
              background: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr style={{ borderBottom: '1px solid #eee' }}>
      <td style={{ padding: '0.75rem' }}>{member.Name}</td>
      <td style={{ padding: '0.75rem', fontSize: '0.9rem', color: '#666' }}>{member.Email}</td>
      <td style={{ padding: '0.75rem', textAlign: 'center' }}>{member.WeeklyHours}h</td>
      <td style={{ padding: '0.75rem', textAlign: 'center' }}>{member.ProjectAllocationPercent}%</td>
      <td style={{ padding: '0.75rem', textAlign: 'center', fontWeight: 600, color: '#0066cc' }}>
        {projectHours.toFixed(1)}h
      </td>
      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
        <button
          onClick={() => setIsEditing(true)}
          style={{
            padding: '0.5rem 1rem',
            marginRight: '0.5rem',
            background: '#0066cc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9rem',
          }}
        >
          Edit
        </button>
        <button
          onClick={onToggleExpand}
          style={{
            padding: '0.5rem 1rem',
            background: '#17a2b8',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9rem',
          }}
        >
          {isExpanded ? '▼ Availability' : '▶ Availability'}
        </button>
      </td>
    </tr>
  );
}
